/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '@/App';
import {
  BookOpen, UserPlus, User, Users, Briefcase, Calendar,
  MessageSquare, Sparkles, Megaphone, CreditCard, Shield,
  LifeBuoy, ChevronRight, ExternalLink, CheckCircle2, Info,
  AlertTriangle, Menu, X
} from 'lucide-react';

const SECTIONS = [
  { id: 'getting-started', label: 'Getting Started', icon: UserPlus },
  { id: 'your-profile', label: 'Your Profile', icon: User },
  { id: 'members', label: 'Members Directory', icon: Users },
  { id: 'businesses', label: 'Businesses', icon: Briefcase },
  { id: 'events', label: 'Events', icon: Calendar },
  { id: 'forum', label: 'Community Forum', icon: MessageSquare },
  { id: 'opportunities', label: 'Opportunities', icon: Sparkles },
  { id: 'advertisements', label: 'Advertisements', icon: Megaphone },
  { id: 'membership', label: 'Membership & Billing', icon: CreditCard },
  { id: 'privacy', label: 'Privacy & Your Data', icon: Shield },
  { id: 'help', label: 'Getting Help', icon: LifeBuoy },
];

/* ---------- Reusable pieces ---------- */
const Tip = ({ children }) => (
  <div className="flex gap-3 p-4 my-4 rounded-lg bg-rose-50 border border-rose-100">
    <Info className="w-5 h-5 text-rose-700 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-rose-900">{children}</p>
  </div>
);

const Warn = ({ children }) => (
  <div className="flex gap-3 p-4 my-4 rounded-lg bg-amber-50 border border-amber-200">
    <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-amber-900">{children}</p>
  </div>
);

const Step = ({ n, title, children }) => (
  <div className="flex gap-4 my-4">
    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-700 text-white text-sm font-bold flex items-center justify-center">{n}</div>
    <div className="flex-1">
      <p className="font-semibold text-gray-900">{title}</p>
      <div className="text-gray-700 text-sm mt-1">{children}</div>
    </div>
  </div>
);

const PageLink = ({ to, children }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-1 text-rose-700 hover:text-rose-800 font-semibold underline underline-offset-2"
    data-testid={`guide-link-${to.replace(/[^a-z0-9]/gi, '-')}`}
  >
    {children}
    <ExternalLink className="w-3.5 h-3.5" />
  </Link>
);

const Section = ({ id, icon: Icon, title, subtitle, children }) => (
  <section id={id} className="scroll-mt-24 mb-16" data-testid={`guide-section-${id}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-800 flex items-center justify-center">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-3xl font-serif text-rose-900">{title}</h2>
    </div>
    {subtitle && <p className="text-gray-600 mb-4">{subtitle}</p>}
    <div className="prose prose-rose max-w-none text-gray-800 leading-relaxed">{children}</div>
  </section>
);

/* ---------- Main page ---------- */
const UserGuide = () => {
  const { user } = useContext(AuthContext);
  const [active, setActive] = useState('getting-started');
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px', threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white" data-testid="user-guide-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-rose-900 to-rose-700 text-white py-14">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center gap-3 mb-3">
            <BookOpen className="w-8 h-8" />
            <span className="uppercase text-xs tracking-widest text-rose-200">User Guide</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif mb-3">Welcome to Her Enterprise Hub</h1>
          <p className="text-rose-100 max-w-2xl">
            Everything you need to get set up, connect with fellow women in business, and make the most of your membership.
            Use the menu on the left to jump to any section.
          </p>
          {user?.role === 'admin' && (
            <Link
              to="/admin/guide"
              className="inline-flex items-center gap-2 mt-6 bg-white text-rose-800 px-4 py-2 rounded-full text-sm font-semibold hover:bg-rose-50 transition-colors"
              data-testid="user-guide-admin-guide-link"
            >
              You're an admin, see the Admin Guide →
            </Link>
          )}
        </div>
      </div>

      {/* Mobile ToC toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed bottom-6 right-6 z-40 bg-rose-700 text-white p-3 rounded-full shadow-lg"
        data-testid="guide-mobile-toc-toggle"
        aria-label="Toggle contents"
      >
        {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      <div className="container mx-auto px-4 max-w-6xl py-10">
        <div className="flex gap-8">
          {/* Sidebar / ToC */}
          <aside
            className={`
              lg:sticky lg:top-24 lg:h-fit lg:w-64 lg:flex-shrink-0 lg:block
              ${mobileOpen ? 'fixed inset-0 z-30 bg-white p-6 pt-20 overflow-y-auto' : 'hidden'}
            `}
            data-testid="guide-toc"
          >
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Contents</p>
            <nav className="space-y-1">
              {SECTIONS.map((s) => {
                const IconEl = s.icon;
                const isActive = active === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    data-testid={`guide-toc-${s.id}`}
                    className={`
                      w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors
                      ${isActive
                        ? 'bg-rose-100 text-rose-900 font-semibold'
                        : 'text-gray-700 hover:bg-rose-50 hover:text-rose-800'}
                    `}
                  >
                    <IconEl className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1">{s.label}</span>
                    {isActive && <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">

            <Section id="getting-started" icon={UserPlus} title="Getting Started"
              subtitle="Signing up, verifying your email, and logging in.">
              <p>
                Her Enterprise Hub is free to join. Head to the <PageLink to="/">homepage</PageLink> and click{' '}
                <strong>Join Our Community</strong> to create your account.
              </p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Sign up in 3 steps</h3>
              <Step n="1" title="Fill in the sign-up form">
                Enter your name, email, and business name. Choose a strong password
                (minimum 12 characters, with one uppercase letter, one lowercase, one number, and one special character
                from <code className="text-xs bg-gray-100 px-1 rounded">!@#$%^&amp;*()_+-=[]{"{}"}|;:,.&lt;&gt;?"'</code>).
              </Step>
              <Step n="2" title="Verify your email">
                We send you a confirmation link. Click it to activate your account.
                If you don't see it within a few minutes, check your Spam or Junk folder.
              </Step>
              <Step n="3" title="Sign in and land on your dashboard">
                Use the <strong>Sign In</strong> button at the top-right of the homepage.
                Prefer Google or LinkedIn? Use the social sign-in buttons on the login screen.
              </Step>

              <Tip>
                Locked out? After 5 wrong password attempts your account is temporarily locked for 15 minutes for security.
                Just wait it out, or use <strong>"Forgot password?"</strong> to reset.
              </Tip>

              <div className="mt-6 p-5 rounded-xl bg-rose-50 border border-rose-100">
                <p className="text-sm text-rose-900 font-semibold mb-2">Missed the welcome tour?</p>
                <p className="text-sm text-gray-700 mb-3">
                  Every new member gets a quick 60-second walkthrough the first time they land on the dashboard.
                  You can replay it any time.
                </p>
                <Link
                  to="/dashboard?tour=1"
                  className="inline-block bg-rose-800 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-rose-900 transition-colors"
                  data-testid="user-guide-replay-tour-link"
                >
                  Replay the welcome tour →
                </Link>
              </div>
            </Section>

            <Section id="your-profile" icon={User} title="Your Profile"
              subtitle="Your profile is how the community discovers and remembers you.">
              <p>
                Head to your Profile page any time via the top navigation. A complete profile gets far more visibility
                in the Members Directory and search results.
              </p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">What to fill in</h3>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li><strong>Profile photo</strong>: a clear headshot (JPG or PNG, under 2 MB)</li>
                <li><strong>Business name &amp; industry</strong>: helps others find you</li>
                <li><strong>Bio</strong>: a short paragraph on who you are and what you offer</li>
                <li><strong>Location</strong>: city and country</li>
                <li><strong>Social links</strong>: website, LinkedIn, Instagram, X (Twitter)</li>
                <li><strong>Call-to-action</strong>: e.g. "Book a discovery call" pointing to a link you choose</li>
              </ul>

              <Tip>
                Every field you fill in bumps up your <em>profile completeness score</em>. Members with 80%+ appear higher in the directory.
              </Tip>
            </Section>

            <Section id="members" icon={Users} title="Members Directory"
              subtitle="Browse, search, and connect with women in the community.">
              <p>
                Visit the <PageLink to="/members">Members Directory</PageLink> to see every member.
                Search by name, industry, or business. Click any member card to view their full profile,
                or use the message icon to start a direct conversation.
              </p>
              <ul className="list-disc ml-6 space-y-1 text-sm mt-3">
                <li><strong>View Profile</strong>: see bio, business info, gallery, social links, featured post</li>
                <li><strong>Message icon</strong>: send a private message (both members must be signed in)</li>
                <li><strong>Search bar</strong>: instant filter as you type</li>
              </ul>
            </Section>

            <Section id="businesses" icon={Briefcase} title="Businesses"
              subtitle="Showcase what you do or discover others' offerings.">
              <p>
                The <PageLink to="/businesses">Businesses</PageLink> page lists every business created by our members.
                Add your own business to be discovered.
              </p>
              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Adding a business</h3>
              <Step n="1" title="Open Businesses → Add Business">
                Click the pink <strong>+ Add Business</strong> button in the top-right.
              </Step>
              <Step n="2" title="Fill in the details">
                Business name, category, description, logo (max 2 MB, JPG or PNG), website, contact info, and services offered.
              </Step>
              <Step n="3" title="Publish">
                Your business appears in the directory immediately. You can edit or delete it any time from the same page.
              </Step>
              <Warn>
                Businesses are <strong>publicly visible</strong>: anyone can view them, even without logging in.
                Never include information you wouldn't share on your public website.
              </Warn>
            </Section>

            <Section id="events" icon={Calendar} title="Events"
              subtitle="Discover, RSVP to, and host community events.">
              <p>
                Head to <PageLink to="/events">Events</PageLink> to see all upcoming meetups, masterclasses,
                webinars, and networking sessions. Switch between <strong>List View</strong> and <strong>Calendar View</strong>
                at the top.
              </p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">RSVP to an event</h3>
              <p>Click the pink <strong>RSVP</strong> button on any event card. You'll get a confirmation email and a
              reminder as the date approaches.</p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Host your own event</h3>
              <p>
                Premium and Corporate members can host events. Click <strong>+ Create Event</strong> in the top-right,
                fill in title, date, description, location (physical, online, or hybrid), and pricing (free or paid).
              </p>
              <Tip>
                Events you host can be free or paid. Paid events are processed securely through Stripe, you'll receive payouts to your linked bank account.
              </Tip>
            </Section>

            <Section id="forum" icon={MessageSquare} title="Community Forum"
              subtitle="Ask questions, share wins, get advice from fellow founders.">
              <p>
                The <PageLink to="/forum">Forum</PageLink> is where the community talks: questions, celebrations, tips,
                and support. Posts are organized by category.
              </p>
              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Starting a discussion</h3>
              <ol className="list-decimal ml-6 space-y-1 text-sm">
                <li>Click <strong>+ New Post</strong></li>
                <li>Pick a category (General, Advice, Wins, Q&amp;A, etc.)</li>
                <li>Write a clear title and body, formatting is supported (bold, italic, links)</li>
                <li>Publish. Others can comment and react.</li>
              </ol>
              <Tip>Be kind, be specific, and keep it constructive. Our community guidelines apply to every post and comment.</Tip>
            </Section>

            <Section id="opportunities" icon={Sparkles} title="Opportunities"
              subtitle="Jobs, collaborations, speaking slots, mentorship, and more.">
              <p>
                The <PageLink to="/opportunities">Opportunities Board</PageLink> is a curated list of gigs and openings
                shared by the community. Filter by type: job, collaboration, mentorship, speaker, etc.
              </p>
              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Applying</h3>
              <p>Click <strong>Apply</strong> on any card, add a short note, and send. The poster receives your message and can respond directly.</p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Posting your own</h3>
              <p>
                Click <strong>+ Post Opportunity</strong>. Free members can post up to 5 opportunities/month.
                Premium and Corporate members get unlimited posts and featured placement.
              </p>
            </Section>

            <Section id="advertisements" icon={Megaphone} title="Advertisements"
              subtitle="Reach the whole community with a featured ad.">
              <p>
                Premium and Corporate members can promote their business, event, or offer via paid advertisements
                displayed on the homepage and throughout the platform.
              </p>
              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Creating an ad</h3>
              <Step n="1" title="Choose a placement">
                Homepage spotlight, featured business, or in-content banner. Each has different visibility and pricing.
              </Step>
              <Step n="2" title="Design your ad">
                Upload an image (recommended 1200×630 px, under 2 MB), write a short headline and description,
                add your target link.
              </Step>
              <Step n="3" title="Pay and submit for approval">
                Ads go through a quick moderation review (usually within 24 hours). Once approved, your ad goes live for the duration you paid for.
              </Step>
              <Tip>
                Your Profile page shows analytics for each of your ads: views, clicks, and click-through rate. Use this to iterate on what works.
              </Tip>
            </Section>

            <Section id="membership" icon={CreditCard} title="Membership & Billing"
              subtitle="Choose the plan that fits where you are in your business journey.">
              <p>
                Compare all plans on the <PageLink to="/membership">Membership</PageLink> page. Every plan can be
                billed <strong>monthly</strong> or <strong>yearly (save 17%)</strong>.
              </p>

              <div className="grid md:grid-cols-2 gap-3 my-4">
                <div className="p-4 rounded-lg border border-gray-200 bg-white">
                  <p className="font-semibold text-rose-900">Free</p>
                  <p className="text-sm text-gray-600">Basic profile, view up to 5 members, attend free events.</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-white">
                  <p className="font-semibold text-rose-900">Basic, €25/mo</p>
                  <p className="text-sm text-gray-600">Full directory, forums, networking. For growing businesses.</p>
                </div>
                <div className="p-4 rounded-lg border-2 border-rose-500 bg-rose-50">
                  <p className="font-semibold text-rose-900">Premium, €50/mo <span className="text-xs bg-rose-700 text-white px-2 py-0.5 rounded-full ml-1">Popular</span></p>
                  <p className="text-sm text-gray-700">Host events, post ads, priority support, exclusive masterclasses.</p>
                </div>
                <div className="p-4 rounded-lg border border-gray-200 bg-white">
                  <p className="font-semibold text-rose-900">Corporate, €120/mo</p>
                  <p className="text-sm text-gray-600">Dedicated account management, VIP events, brand partnership access.</p>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Upgrading or downgrading</h3>
              <p>
                On <PageLink to="/membership">Membership</PageLink> click your preferred plan. Payment is processed by
                Stripe. Change or cancel any time, no long-term commitment.
              </p>
              <Tip>
                As an EU consumer, you have a 14-day right of withdrawal on any paid membership.
                Cancel within 14 days of first purchase for a full refund. After that, cancellations take effect at the end of your current billing period.
              </Tip>
            </Section>

            <Section id="privacy" icon={Shield} title="Privacy & Your Data"
              subtitle="Your rights under GDPR and how we protect your data.">
              <p>
                Her Enterprise Hub is built with GDPR in mind. Read the full policy on our{' '}
                <PageLink to="/datenschutz">Datenschutz page</PageLink> (Privacy Policy).
              </p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">Your rights</h3>
              <ul className="list-disc ml-6 space-y-1 text-sm">
                <li><strong>Access</strong>: request a copy of the data we hold about you</li>
                <li><strong>Rectification</strong>: correct anything that's wrong (or edit it yourself on your profile)</li>
                <li><strong>Deletion</strong>: remove your account and personal data ("right to be forgotten")</li>
                <li><strong>Portability</strong>: export your data in a machine-readable format</li>
                <li><strong>Objection</strong>: opt out of certain uses</li>
                <li><strong>Complaint</strong>: lodge a complaint with the Bavarian Data Protection Authority (BayLDA)</li>
              </ul>

              <p className="mt-4">
                To exercise any right, email{' '}
                <a href="mailto:info@herenterprisehub.com" className="text-rose-700 underline font-semibold">
                  info@herenterprisehub.com
                </a>. We respond within 30 days.
              </p>

              <h3 className="text-lg font-semibold text-rose-900 mt-6 mb-2">How we keep your data safe</h3>
              <div className="grid sm:grid-cols-2 gap-2 my-3">
                {[
                  'HTTPS/TLS encryption for all traffic',
                  'Bcrypt password hashing',
                  'Account lockout after 5 failed logins',
                  'XSS and CSRF protection',
                  'Rate limiting against brute-force attacks',
                  'Security audit logs',
                  'File upload validation (size + type)',
                  'Emails routed through EU-region infrastructure',
                ].map((f) => (
                  <div key={f} className="flex gap-2 items-start text-sm text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="help" icon={LifeBuoy} title="Getting Help"
              subtitle="We're here when you need us.">
              <p>Stuck on something? A few ways to get support:</p>
              <ul className="list-disc ml-6 space-y-2 text-sm mt-3">
                <li>
                  <strong>Contact us</strong>: use our <PageLink to="/contact">Contact page</PageLink> for
                  any question or feedback.
                </li>
                <li>
                  <strong>Email</strong> -{' '}
                  <a href="mailto:info@herenterprisehub.com" className="text-rose-700 underline font-semibold">
                    info@herenterprisehub.com
                  </a>
                </li>
                <li>
                  <strong>Community forum</strong>: often the fastest way to get an answer. Fellow members are generous with advice.
                </li>
                <li>
                  <strong>Premium &amp; Corporate members</strong>: you get priority support and (Corporate) a dedicated account manager.
                </li>
              </ul>

              <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-rose-900 to-rose-700 text-white">
                <p className="text-lg font-serif mb-1">Ready to build, collaborate, and lead?</p>
                <p className="text-rose-100 text-sm mb-4">
                  You're now equipped with everything you need to make the most of Her Enterprise Hub.
                </p>
                <Link
                  to="/dashboard"
                  className="inline-block bg-white text-rose-800 px-5 py-2 rounded-full font-semibold hover:bg-rose-50 transition-colors"
                  data-testid="guide-cta-dashboard"
                >
                  Go to my dashboard →
                </Link>
              </div>
            </Section>

          </main>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;
